import { z } from 'zod';

/**
 * Maximum CSV payload size. Realistic CO uploads are tens to a few hundred
 * rows; 512 KB covers ~5000 rows of comfortable narration. Big enough to
 * never be a real-world ceiling, small enough to keep the JSON body cap
 * from sneaking up on us.
 */
export const TRANSACTION_IMPORT_MAX_CSV_BYTES = 512 * 1024;

/**
 * Per-row hard cap on transactions imported in a single commit. Belt-and-
 * braces against an accidental "the whole-year ledger" upload. Well above
 * any realistic single-batch entry size.
 */
export const TRANSACTION_IMPORT_MAX_ROWS = 5_000;

/**
 * CSV import upload payload. Frontend reads the file as text via
 * `FileReader.readAsText`, hashes the original filename for audit, and
 * posts both fields here. The actual file is never multipart-uploaded —
 * keeping the API JSON-only avoids a second body parser.
 *
 * `dryRun` defaults to true so an accidental call doesn't write rows;
 * an explicit `dryRun=false` is required to commit.
 */
export const transactionImportRequestSchema = z.object({
  csv: z
    .string({ message: 'CSV payload is required' })
    .min(1, 'CSV cannot be empty')
    .max(
      TRANSACTION_IMPORT_MAX_CSV_BYTES,
      `CSV cannot exceed ${String(TRANSACTION_IMPORT_MAX_CSV_BYTES)} bytes`,
    ),
  filename: z
    .string({ message: 'Filename is required' })
    .min(1, 'Filename cannot be empty')
    .max(200, 'Filename must be at most 200 characters'),
  dryRun: z.boolean().default(true),
});
export type TransactionImportRequest = z.infer<typeof transactionImportRequestSchema>;

export interface TransactionImportRowParsed {
  fundCode: string;
  fundId: string;
  subHeadCode: string | null;
  subHeadId: string | null;
  voucherNo: string;
  transactionDate: string;
  direction: 'CREDIT' | 'DEBIT';
  amount: string;
  narration: string;
}

export interface TransactionImportRowResult {
  /** 1-indexed CSV row number, header excluded (so first data row = 1). */
  rowNumber: number;
  /** Raw cells keyed by header — surfaced so the UI can echo problem rows back. */
  raw: Record<string, string>;
  /** Empty when the row is valid. */
  errors: string[];
  /** Populated only when `errors.length === 0`. */
  parsed: TransactionImportRowParsed | null;
}

export interface TransactionImportPreview {
  filename: string;
  /** SHA-256 of the CSV payload, hex. Logged in the audit entry on commit. */
  sha256: string;
  rowsParsed: number;
  rowsValid: number;
  rowsWithErrors: number;
  totalCredits: string;
  totalDebits: string;
  rows: TransactionImportRowResult[];
  /** True only when dryRun=false AND every row validates AND the insert ran. */
  committed: boolean;
  /** UUIDs of created transactions, in CSV row order. Empty unless committed. */
  createdTransactionIds: string[];
}

/**
 * Canonical CSV header order. Frontends, sample templates, and the parser
 * all source the column names from here so renaming requires one edit.
 */
export const TRANSACTION_IMPORT_HEADERS = [
  'voucher_no',
  'transaction_date',
  'fund_code',
  'sub_head_code',
  'direction',
  'amount',
  'narration',
] as const;
